import styled from "@emotion/styled";
import { fontSizes } from "../typography";
import { fontWeight } from "@atlas-int/tokens";
import shipSvg from "../../assets/ship.svg";
import shipASvg from "../../assets/shipA.svg";
import shipBSvg from "../../assets/shipB.svg";
import shipADiscrepancySvg from "../../assets/shipA-discrepancy.svg";
import shipBDiscrepancySvg from "../../assets/shipB-discrepancy.svg";
import reporterSvg from "../../assets/reporter.svg";

const IntroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  padding-bottom: 2em;
  box-sizing: border-box;
  flex-grow: 1;
`;

const IntroTitleContainer = styled.div`
  text-align: center;
  letter-spacing: 0.5px;
  padding-bottom: 1em;
`;

const IntroTitle = styled.span`
  font-weight: ${fontWeight.semibold};
  color: rgb(77, 77, 77);
  text-transform: uppercase;
  font-size: ${fontSizes.vizTitle1};
  display: inline-block;
`;

const IntroMainViz = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const IntroContainer = styled.div<{ size: "narrow" | "wide"; height: number }>`
  flex-grow: ${({ size }) => (size === "narrow" ? 1 : 0)};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  aspect-ratio: 1;
  max-height: ${({ size, height }) =>
    size === "wide" ? `${height * 0.4}px` : "none"};
  max-width: ${({ size, height }) =>
    size === "wide" ? `${height * 0.4}px` : "none"};
`;

const IntroLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 1em;
`;

const ReporterLabel = styled.div<{ shown: boolean }>`
  display: flex;
  visibility: ${({ shown }) => (shown ? "visible" : "hidden")};
  font-weight: ${fontWeight.semibold};
  text-transform: uppercase;
  gap: 0.5rem;
  line-height: 1.15;
  padding-bottom: 0.75em;
  padding-left: 1em;
  margin: 0 1.5em;
  font-size: ${fontSizes.reporterLabel};
  letter-spacing: 0.5px;
`;

const CountryLabel = styled.span<{
  country: "A" | "B";
  size: "narrow" | "wide";
  transparent: boolean;
}>`
  background-color: ${({ country }) =>
    country === "A" ? "#69d7a7" : "#5e8bce"};
  opacity: ${({ transparent }) => (transparent ? 0.5 : 1)};
  border-radius: 1.5em;
  padding: 0.35em 1.25em;
  margin: 0 1.5em;
  text-transform: uppercase;
  color: white;
  font-weight: ${fontWeight.semibold};
  font-size: ${({ size }) =>
    size === "wide" ? fontSizes.countryLabel : fontSizes.countryLabelNarrow};
  text-wrap: nowrap;
  display: inline-block;
  width: fit-content;
  align-self: center;
`;

const IntroLegend = styled.div`
  padding: 1em 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const DiscrepancyLabel = styled.p<{ shown: boolean }>`
  display: flex;
  visibility: ${({ shown }) => (shown ? "visible" : "hidden")};
  font-weight: ${fontWeight.semibold};
  color: #d669bf;
  padding: 0;
  margin: 0.25em;
`;

const LegendContainer = styled.div<{ shown: boolean }>`
  display: flex;
  visibility: ${({ shown }) => (shown ? "visible" : "hidden")};
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.25em;
  font-size: ${fontSizes.discrepancyLabel};
`;

const LegendSquare = styled.svg`
  flex-shrink: 0;
`;

const ColoredAmount = styled.span<{ amount: "603M" | "1.1B" }>`
  color: ${({ amount }) =>
    amount === "603M" ? "rgb(75, 139, 81)" : "rgb(70, 134, 163)"};
`;

const colorizeAmount = (text: string) => {
  const parts = text.split(/\$(?:603M|1\.1B)/g);
  const amounts = text.match(/\$(?:603M|1\.1B)/g) || [];

  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {amounts[i] && (
        <>
          <ColoredAmount amount={amounts[i] === "$603M" ? "603M" : "1.1B"}>
            {amounts[i]}
          </ColoredAmount>
          {amounts[i] === "$603M" && <br />}
        </>
      )}
    </span>
  ));
};

const IntroTitles = {
  "": "Trade Partners",
  init: "Trade Partners",
  "problem-1-header": "Trade Partners",
  intro: "Trade Partners",
  "country-A-pov": "Country A's Exports to B: $603M",
  "country-B-pov": "Country B's Imports from A: $1.1B",
  discrepancy:
    "Country A reports exports of $603M while Country B reports imports of $1.1B",
  discrepancy2:
    "Country A reports exports of $603M while Country B reports imports of $1.1B ",
  toggle:
    "Country A reports exports of $603M while Country B reports imports of $1.1B",
  scale:
    "Country A reports exports of $603M while Country B reports imports of $1.1B",
};

const IntroImages = {
  "": shipSvg,
  init: shipSvg,
  "problem-1-header": shipSvg,
  intro: shipSvg,
  "country-A-pov": shipASvg,
  "country-B-pov": shipBSvg,
  discrepancy: shipADiscrepancySvg,
  discrepancy2: shipBDiscrepancySvg,
  toggle: shipBDiscrepancySvg,
  scale: shipBDiscrepancySvg,
};

type IntroStep = keyof typeof IntroTitles;

export default function IntroVisualization({
  currentStep,
  size,
  height,
}: {
  currentStep: IntroStep;
  size: "narrow" | "wide";
  height: number;
}) {
  let highlightA =
    currentStep === "country-A-pov" || currentStep === "discrepancy";

  let highlightB = [
    "country-B-pov",
    "discrepancy2",
    "toggle",
    "scale",
  ].includes(currentStep);

  let showDiscrepancy = [
    "discrepancy",
    "discrepancy2",
    "toggle",
    "scale",
  ].includes(currentStep);

  let discrepancy = currentStep === "discrepancy" ? "less" : "more";

  return (
    <IntroWrapper>
      <IntroTitleContainer>
        <IntroTitle>{colorizeAmount(IntroTitles[currentStep])}</IntroTitle>
      </IntroTitleContainer>
      <IntroMainViz>
        <IntroLabelContainer>
          <ReporterLabel shown={highlightA}>
            <img
              src={reporterSvg}
              alt="reporting country"
              style={{ width: "clamp(1rem, 2vw, 1.5rem)" }}
            />
            Reporting <br /> Country
          </ReporterLabel>
          <CountryLabel country="A" size={size} transparent={highlightB}>
            Country A
          </CountryLabel>
        </IntroLabelContainer>
        <IntroContainer size={size} height={height}>
          <img
            style={{ height: "100%", width: "100%" }}
            src={IntroImages[currentStep]}
            alt="intro visualization"
          />
        </IntroContainer>
        <IntroLabelContainer>
          <ReporterLabel shown={highlightB}>
            <img
              src={reporterSvg}
              alt="reporting country"
              style={{ width: "clamp(1rem, 2vw, 1.5rem)" }}
            />
            Reporting <br /> Country
          </ReporterLabel>
          <CountryLabel country="B" size={size} transparent={highlightA}>
            Country B
          </CountryLabel>
        </IntroLabelContainer>
      </IntroMainViz>
      <IntroLegend>
        <DiscrepancyLabel shown={showDiscrepancy}>
          $497M {discrepancy}
        </DiscrepancyLabel>
        <LegendContainer shown={highlightA || highlightB}>
          <LegendSquare width="16" height="16">
            <rect
              x="0"
              y="0"
              width="16"
              height="16"
              fill={highlightA ? "#69d7a7" : "#5e8bce"}
              stroke={highlightA ? "#69d7a7" : "#5e8bce"}
              strokeWidth={1}
            />
          </LegendSquare>
          <span style={{ color: "#707070" }}>= $30M in trade</span>
        </LegendContainer>
      </IntroLegend>
    </IntroWrapper>
  );
}
